import React from 'react';
import { formatCurrency } from '../utils/i18n';
import { getImageUrl } from '../utils/imageUtils';

/**
 * Unified ProductCard component.
 *
 * Props:
 *   product          — product data object
 *   onClick          — click handler
 *   wishlisted       — boolean
 *   onWishlistToggle — called with (product) when wishlist button clicked
 *   hideCommunity    — hide community row
 *   hideSeller       — hide publisher avatar + name (útil cuando ya estamos
 *                      en la página del usuario)
 */
function ProductCard({ product, onClick, wishlisted, onWishlistToggle, hideCommunity = false, hideSeller = false, variant = 'default' }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  const variantClasses = {
    default: 'bg-surface border border-outline rounded-lg shadow-sm hover:shadow-md',
    mercadolibre: 'bg-white border border-black/[0.04] rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.10)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.14)]',
    glass: 'bg-white/80 backdrop-blur-lg rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04] border border-black/[0.06] hover:shadow-[0_12px_40px_rgba(0,0,0,0.15),0_4px_12px_rgba(0,0,0,0.10)]',
    'glass-subtle': 'bg-white/70 backdrop-blur-md rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.07),0_1px_4px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.03] border border-black/[0.04] hover:shadow-[0_6px_22px_rgba(0,0,0,0.10),0_2px_6px_rgba(0,0,0,0.07)]',
  };
  const outerClass = variantClasses[variant] || variantClasses.default;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={`${outerClass} transition-all duration-200 cursor-pointer flex flex-col h-full group overflow-hidden`}
    >
      {/* Seller + Community (top row) — se omite entera si tanto seller
          como community están ocultos para no dejar padding muerto. */}
      {(!hideSeller || (!hideCommunity && product.sharedCommunityNames?.length > 0)) && (
        <div className="flex items-center gap-2 px-3 py-2">
          {!hideSeller && (
            <>
              {product.publisherProfilePictureId ? (
                <img
                  src={getImageUrl(product.publisherProfilePictureId)}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="w-5 h-5 rounded-full bg-primary-container flex items-center justify-center shrink-0 text-[10px] font-bold text-on-primary-container">
                  {(product.publisherName || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-[11px] text-on-surface-variant font-medium truncate flex-1">
                {product.publisherName}
              </span>
            </>
          )}
          {!hideCommunity && product.sharedCommunityNames?.length > 0 && (
            <span className={`text-[10px] text-on-surface-variant/70 bg-surface-container-high px-1.5 py-0.5 rounded-full truncate max-w-[90px] shrink-0 flex items-center gap-0.5 ${hideSeller ? 'ml-auto' : ''}`}>
              <span className="truncate">{product.sharedCommunityNames[0]}</span>
              {product.sharedCommunityNames.length > 1 && (
                <span className="shrink-0 text-on-surface-variant/50">+{product.sharedCommunityNames.length - 1}</span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-surface-container">
        {product.imageId ? (
          <img
            alt={product.name}
            className="w-full h-full object-cover"
            src={getImageUrl(product.imageId)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="material-symbols-outlined text-5xl text-outline-variant">image</span>
          </div>
        )}
        {/* Wishlist button */}
        {onWishlistToggle && (
          <button
            aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            onClick={(e) => { e.stopPropagation(); onWishlistToggle(product); }}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white transition-colors"
          >
            <span
              className="material-symbols-outlined text-[18px]"
              style={{
                color: wishlisted ? '#ef4444' : '#6b7280',
                fontVariationSettings: wishlisted ? "'FILL' 1" : "'FILL' 0",
              }}
            >
              favorite
            </span>
          </button>
        )}
      </div>

      {/* Name + Price */}
      <div className="px-3 py-2.5 flex flex-col gap-1">
        <h3 className="font-medium text-sm text-on-surface line-clamp-2 leading-snug">
          {product.name}
        </h3>
        <p className="text-base font-bold text-primary leading-tight">
          {formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </p>
      </div>
    </div>
  );
}

export default ProductCard;
